import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import {
  boatWorldcatAerial,
  heroBluefinCloseup,
  heroBluefinDeck,
  heroUnderwaterBluefin,
  heroWsbDiverKelpDark,
  heroWsbKelp,
} from '../../assets/images/hero/imageAssets';
import SEOHead from '../../components/SEOHead';
import { getServiceSchema, getOgImageUrl, OG_IMAGE_DIMENSIONS } from '../../utils/seoSchemas';
import {
  CtaButton,
  CtaContainer,
  CtaSection,
  CtaSubtext,
  CtaTitle,
  DestinationCard,
  DestinationContent,
  DestinationCta,
  DestinationDescription,
  DestinationImage,
  DestinationsContainer,
  DestinationsGrid,
  DestinationsSection,
  DestinationSpecies,
  DestinationTagline,
  DestinationTitle,
  FeaturedBadge,
  FeaturedCard,
  FeaturedContainer,
  FeaturedContent,
  FeaturedCta,
  FeaturedDescription,
  FeaturedImage,
  FeaturedSection,
  FeaturedSpecies,
  FeaturedTitle,
  HeroButton,
  HeroEyebrow,
  HeroInner,
  HeroSubtitle,
  HeroTitle,
  PageHero,
  SectionEyebrow,
  SectionTitle,
  SpeciesTag,
  StatItem,
  StatLabel,
  StatsContainer,
  StatsStrip,
  StatValue,
} from './styles/SpearfishingOverview.styles';

const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const SpearfishingOverviewPage = () => {
  return (
    <>
      <SEOHead
        title="Spearfishing Charters — Southern California | Fussaki"
        description="Premium freedive spearfishing charters from San Pedro, CA. Catalina Island, San Clemente Island, kelp forests, open blue water. White Seabass, Yellowtail, Bluefin Tuna. Book with Captain Christos."
        canonical="https://fussaki.com/charters/spearfishing"
        ogImage={getOgImageUrl(heroWsbDiverKelpDark)}
        ogImageWidth={OG_IMAGE_DIMENSIONS.heroWsbDiverKelpDark.width}
        ogImageHeight={OG_IMAGE_DIMENSIONS.heroWsbDiverKelpDark.height}
        jsonLd={getServiceSchema(
          'Spearfishing Charters — Southern California',
          'Freedive spearfishing charters targeting White Seabass, Yellowtail, Bluefin Tuna, and more across Southern California waters.',
          '/charters/spearfishing'
        )}
      />

      {/* ─── Hero — Left-aligned, bottom-weighted ─── */}
      <PageHero $bgImage={heroWsbDiverKelpDark} role="img" aria-label="Spearfisher with White Seabass swimming through dark kelp forest">
        <HeroInner>
          <HeroEyebrow>Spearfishing Charters</HeroEyebrow>
          <HeroTitle>Hunt Southern California's <span>Best Waters</span></HeroTitle>
          <HeroSubtitle>
            Freedive spearfishing out of San Pedro aboard the WorldCat 295CC.
            Captain Christos reads the conditions and puts you where the fish are —
            from Catalina's kelp forests to San Clemente's deep reefs to open blue water.
          </HeroSubtitle>
          <HeroButton to="/book">
            Book Your Charter {FaArrowRight({ size: 14 })}
          </HeroButton>
        </HeroInner>
      </PageHero>

      {/* ─── Stats Strip ─── */}
      <StatsStrip>
        <StatsContainer>
          <StatItem>
            <StatValue>5</StatValue>
            <StatLabel>Destinations</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>12+</StatValue>
            <StatLabel>Species Targeted</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>6</StatValue>
            <StatLabel>Max Per Trip</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>4-12hr</StatValue>
            <StatLabel>Trip Options</StatLabel>
          </StatItem>
        </StatsContainer>
      </StatsStrip>

      {/* ─── Featured Destination: Catalina ─── */}
      <FeaturedSection>
        <FeaturedContainer>
          <AnimatedSection>
            <SectionEyebrow>Featured Destination</SectionEyebrow>
            <SectionTitle>Where We Hunt</SectionTitle>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            <FeaturedCard to="/charters/spearfishing/catalina" $bgImage={boatWorldcatAerial}>
              <FeaturedImage $bgImage={boatWorldcatAerial} />
              <FeaturedContent>
                <FeaturedBadge>Most Popular</FeaturedBadge>
                <FeaturedTitle>Catalina Island</FeaturedTitle>
                <FeaturedDescription>
                  The crown jewel of Southern California spearfishing. 26 miles across the channel,
                  Catalina delivers crystal-clear visibility, massive kelp forests, and fish density
                  you won't find anywhere on the mainland. White Seabass ghost through the kelp canopy,
                  Yellowtail run the structure, and Calico Bass hold on every rock.
                </FeaturedDescription>
                <FeaturedSpecies>
                  <SpeciesTag>White Seabass</SpeciesTag>
                  <SpeciesTag>Yellowtail</SpeciesTag>
                  <SpeciesTag>Calico Bass</SpeciesTag>
                  <SpeciesTag>Sheephead</SpeciesTag>
                  <SpeciesTag>Halibut</SpeciesTag>
                </FeaturedSpecies>
                <FeaturedCta>
                  Explore Catalina Charters {FaArrowRight({ size: 14 })}
                </FeaturedCta>
              </FeaturedContent>
            </FeaturedCard>
          </AnimatedSection>
        </FeaturedContainer>
      </FeaturedSection>

      {/* ─── Destination Cards — Image-forward tall cards ─── */}
      <DestinationsSection>
        <DestinationsContainer>
          <DestinationsGrid>
            {[
              {
                image: heroUnderwaterBluefin,
                tagline: 'Deep water. Big fish. Remote hunting.',
                title: 'San Clemente Island',
                description: '55 miles offshore — pristine reefs, minimal pressure, trophy-class fish. A full-commitment trip for serious hunters.',
                species: ['White Seabass', 'Yellowtail', 'Bluefin Tuna', 'Dorado'],
                link: '/charters/spearfishing/san-clemente-island',
              },
              {
                image: heroBluefinCloseup,
                tagline: 'Pelagic territory. No bottom. No limits.',
                title: 'Open Blue Water',
                description: 'Offshore drift hunting for Bluefin Tuna, Dorado, and Yellowtail. Satellite-guided, conditions-driven — when the fish push through, we go.',
                species: ['Bluefin Tuna', 'Dorado', 'Yellowtail'],
                link: '/charters/spearfishing/open-blue-water',
              },
              {
                image: heroWsbKelp,
                tagline: 'The stalking grounds.',
                title: 'Kelp Paddies & Kelp Forests',
                description: 'Nearshore kelp canopies and offshore drifters. Where White Seabass ghost through the stalks and Yellowtail cruise the paddies.',
                species: ['White Seabass', 'Yellowtail', 'Calico Bass', 'Halibut'],
                link: '/charters/spearfishing/kelp-forests',
              },
              {
                image: heroBluefinDeck,
                tagline: 'Oil rigs, rock piles, and reef systems.',
                title: 'Major Reefs & Structure',
                description: 'Year-round fish on SoCal\'s artificial and natural reef systems. Great for building skills and half-day hunts when conditions push us off the islands.',
                species: ['Calico Bass', 'Sheephead', 'Yellowtail', 'Lingcod'],
                link: '/charters/spearfishing/reefs',
              },
            ].map((dest, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <DestinationCard to={dest.link} $bgImage={dest.image}>
                  <DestinationImage $bgImage={dest.image} />
                  <DestinationContent>
                    <DestinationTagline>{dest.tagline}</DestinationTagline>
                    <DestinationTitle>{dest.title}</DestinationTitle>
                    <DestinationDescription>{dest.description}</DestinationDescription>
                    <DestinationSpecies>
                      {dest.species.map((sp) => (
                        <SpeciesTag key={sp}>{sp}</SpeciesTag>
                      ))}
                    </DestinationSpecies>
                    <DestinationCta>
                      Explore {FaArrowRight({ size: 12 })}
                    </DestinationCta>
                  </DestinationContent>
                </DestinationCard>
              </AnimatedSection>
            ))}
          </DestinationsGrid>
        </DestinationsContainer>
      </DestinationsSection>

      {/* ─── CTA ─── */}
      <CtaSection $bgImage={heroBluefinDeck} role="img" aria-label="Anglers aboard the WorldCat 295CC with Bluefin Tuna catch">
        <AnimatedSection>
          <CtaContainer>
            <SectionEyebrow style={{ textAlign: 'center' }}>Ready?</SectionEyebrow>
            <CtaTitle>Book Your Spearfishing Charter</CtaTitle>
            <CtaSubtext>
              Check live availability, pick your date, and lock in your spot with Captain Christos.
              Half-day, full-day, and private charter options available.
            </CtaSubtext>
            <CtaButton to="/book">
              Check Availability {FaArrowRight({ size: 16 })}
            </CtaButton>
          </CtaContainer>
        </AnimatedSection>
      </CtaSection>
    </>
  );
};

export default SpearfishingOverviewPage;
